import React from "react";
import { AbsoluteFill, Img, staticFile, useCurrentFrame, interpolate, Easing } from "remotion";
import { COLORS, NEUTRAL, FONT } from "./theme";

// Fondo con degradado navy + malla sutil en movimiento + brillos de color.
export const Backdrop: React.FC<{ tint?: "blue" | "purple" | "mix" }> = ({
  tint = "mix",
}) => {
  const frame = useCurrentFrame();
  const drift = interpolate(frame, [0, 300], [0, 60]);
  const glowBlue =
    tint === "purple" ? 0.12 : 0.28;
  const glowPurple = tint === "blue" ? 0.1 : 0.26;
  return (
    <AbsoluteFill style={{ backgroundColor: COLORS.bg }}>
      <AbsoluteFill
        style={{
          background: `radial-gradient(1200px 700px at 22% 18%, rgba(95,160,228,${glowBlue}), transparent 60%), radial-gradient(1100px 720px at 82% 88%, rgba(175,149,230,${glowPurple}), transparent 60%)`,
        }}
      />
      <AbsoluteFill
        style={{
          opacity: 0.06,
          backgroundImage: `linear-gradient(${NEUTRAL.lineStrong} 1px, transparent 1px), linear-gradient(90deg, ${NEUTRAL.lineStrong} 1px, transparent 1px)`,
          backgroundSize: "72px 72px",
          translate: `${-drift}px ${-drift * 0.4}px`,
        }}
      />
      <AbsoluteFill
        style={{
          background:
            "linear-gradient(180deg, rgba(10,18,32,0) 55%, rgba(10,18,32,0.55) 100%)",
        }}
      />
    </AbsoluteFill>
  );
};

// Marca del producto: logotipo REAL de TuRastreo + bajada "LOGÍSTICA".
export const Brand: React.FC<{ scale?: number; delay?: number }> = ({
  scale = 1,
  delay = 0,
}) => {
  const frame = useCurrentFrame();
  const f = frame - delay;
  const pop = interpolate(f, [0, 22], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.bezier(0.16, 1, 0.3, 1),
  });
  const up = interpolate(f, [0, 26], [22, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.bezier(0.16, 1, 0.3, 1),
  });
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 20 * scale,
        scale: String(pop),
        translate: `0px ${up}px`,
      }}
    >
      <Img
        src={staticFile("brand/logo-turastreo.png")}
        style={{ width: 620 * scale, height: "auto" }}
      />
      <div
        style={{
          fontFamily: FONT,
          fontWeight: 700,
          fontSize: 30 * scale,
          letterSpacing: 12 * scale,
          color: COLORS.blueLight,
          paddingLeft: 12 * scale,
        }}
      >
        LOGÍSTICA
      </div>
    </div>
  );
};

// Icono de pin de ubicación (SVG simple).
export const Pin: React.FC<{ size?: number; color?: string }> = ({
  size = 40,
  color = COLORS.blueLight,
}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path
      d="M12 2C7.9 2 4.5 5.3 4.5 9.4c0 5.3 6.3 11.6 7 12.3.3.3.8.3 1.1 0 .7-.7 7-7 7-12.3C19.5 5.3 16.1 2 12 2Z"
      fill={color}
    />
    <circle cx="12" cy="9.4" r="2.8" fill="rgba(10,18,32,0.85)" />
  </svg>
);

// Etiqueta pequeña en mayúsculas (eyebrow).
export const Kicker: React.FC<{ children: React.ReactNode; color?: string }> = ({
  children,
  color = COLORS.blueLight,
}) => (
  <div
    style={{
      fontFamily: FONT,
      fontWeight: 700,
      fontSize: 26,
      letterSpacing: 6,
      textTransform: "uppercase",
      color,
    }}
  >
    {children}
  </div>
);

// Título de escena grande.
export const Title: React.FC<{
  children: React.ReactNode;
  size?: number;
  delay?: number;
  maxWidth?: number;
}> = ({ children, size = 96, delay = 0, maxWidth = 1500 }) => {
  const frame = useCurrentFrame();
  const f = frame - delay;
  const up = interpolate(f, [0, 26], [40, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.bezier(0.16, 1, 0.3, 1),
  });
  const op = interpolate(f, [0, 22], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <div
      style={{
        fontFamily: FONT,
        fontWeight: 800,
        fontSize: size,
        lineHeight: 1.05,
        letterSpacing: -2,
        color: COLORS.white,
        maxWidth,
        translate: `0px ${up}px`,
        opacity: op,
        textWrap: "balance",
      }}
    >
      {children}
    </div>
  );
};

// Utilidad de aparición (fade + subida) por índice, para listas escalonadas.
export const useReveal = (start: number, dur = 22) => {
  const frame = useCurrentFrame();
  const op = interpolate(frame, [start, start + dur], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const y = interpolate(frame, [start, start + dur], [34, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.bezier(0.16, 1, 0.3, 1),
  });
  return { opacity: op, translate: `0px ${y}px` };
};
